import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "../../auth/AuthProvider";
import { LogViewer } from "../../components/LogViewer";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  DeploymentBadge,
  EmptyState,
  LoadingBlock,
  Mono,
  cx,
} from "../../components/ui";
import { Icon } from "../../components/icons";
import { absoluteTime, duration, relativeTime } from "../../lib/format";
import { useCancelDeployment, useDeploymentLogs, useDeployments } from "../../lib/queries";
import type { Deployment } from "../../lib/types";
import { useAppTabContext } from "./context";

/*
 * History on the left, the selected build on the right — the shape every
 * platform of this kind uses, because a deploy log is read while scanning the
 * list for the one that broke.
 */
/** How many builds to fetch at a time, and how many more each "Show older". */
const PAGE = 25;

export function DeploymentsTab() {
  const { t } = useTranslation("app");
  const { orgId, appId } = useAppTabContext();
  const canCancel = useCan("developer");
  /*
   * A page at a time. The route used to hand back every deployment an app had
   * ever had, which for a busy one is thousands of records to render a list
   * showing twenty.
   */
  const [limit, setLimit] = useState(PAGE);
  const { data, isPending, isPlaceholderData, error } = useDeployments(orgId, appId, limit);
  const deployments = data?.data;
  const total = data?.total ?? 0;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scrollDetailIntoView, setScrollDetailIntoView] = useState(false);
  const detailRef = useRef<HTMLDivElement>(null);
  const cancel = useCancelDeployment(orgId ?? "", appId ?? "");

  // Follow the newest deployment until the operator picks a different one.
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    if (!pinned && deployments && deployments.length > 0) {
      setSelectedId(deployments[0]?.id ?? null);
    }
  }, [deployments, pinned]);

  useEffect(() => {
    if (!scrollDetailIntoView || !selectedId) return;
    if (window.matchMedia("(max-width: 767px)").matches) {
      detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setScrollDetailIntoView(false);
  }, [scrollDetailIntoView, selectedId]);

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  if (!deployments || deployments.length === 0) {
    return (
      <Card>
        <EmptyState
          title={t("deployments.empty")}
          description={t("deployments.emptyDescription")}
        />
      </Card>
    );
  }

  const selected = deployments.find((d) => d.id === selectedId) ?? null;

  return (
    <div className="grid gap-6 md:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
      <Card className="h-fit">
        <CardHeader
          title={t("deployments.history")}
          description={
            total > deployments.length
              ? t("deployments.partial", { shown: deployments.length, total })
              : t("deployments.total", { count: total })
          }
        />
        <ul className="divide-y divide-line">
          {deployments.map((deployment) => (
            <li key={deployment.id}>
              <HistoryRow
                deployment={deployment}
                selected={deployment.id === selectedId}
                onSelect={() => {
                  setPinned(true);
                  setSelectedId(deployment.id);
                  setScrollDetailIntoView(true);
                }}
              />
            </li>
          ))}
        </ul>

        {total > deployments.length && (
          <div className="border-t border-line p-3">
            <Button
              variant="secondary"
              size="sm"
              className="w-full"
              loading={isPlaceholderData}
              onClick={() => setLimit((current) => current + PAGE)}
            >
              {t("deployments.older")}
            </Button>
          </div>
        )}
      </Card>

      {selected && (
        <div ref={detailRef} className="space-y-4">
          <DeploymentHeader
            deployment={selected}
            onCancel={
              canCancel && selected.status === "running"
                ? () => cancel.mutate(selected.id)
                : undefined
            }
            cancelling={cancel.isPending}
          />
          <DeploymentLog orgId={orgId} appId={appId} deploymentId={selected.id} />
        </div>
      )}
    </div>
  );
}

/**
 * One build in the list.
 *
 * The commit leads where there is one — "what shipped" is the question being
 * asked of this list, and a commit answers it where a title like "Manual
 * redeploy" does not. The selected row carries an accent rule on its leading
 * edge rather than only a background, so which one is open survives a glance.
 */
function HistoryRow({
  deployment,
  selected,
  onSelect,
}: {
  deployment: Deployment;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected}
      className={cx(
        "flex w-full flex-col gap-1.5 border-l-2 py-3 pr-4 pl-3.5 text-left transition-colors duration-200 ease-smooth",
        selected ? "border-l-accent bg-raised" : "border-l-transparent hover:bg-raised",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-sm text-fg">
          {deployment.commit ? (
            <>
              <Mono className="text-fg">{deployment.commit.shortSha}</Mono>
              {deployment.commit.message && (
                <span className="ml-2 text-fg-muted">{deployment.commit.message}</span>
              )}
            </>
          ) : (
            deployment.title
          )}
        </span>
        <DeploymentBadge status={deployment.status} />
      </div>

      <div className="flex flex-wrap items-center gap-x-2 text-xs text-fg-subtle">
        <span>{relativeTime(deployment.createdAt)}</span>
        <span aria-hidden="true">·</span>
        <span className="tabular-nums">{duration(deployment.startedAt, deployment.finishedAt)}</span>
        {deployment.triggeredBy && (
          <>
            <span aria-hidden="true">·</span>
            <span className="truncate">{deployment.triggeredBy}</span>
          </>
        )}
      </div>
    </button>
  );
}

/**
 * What this build was and how it went, above its output.
 *
 * The facts sit on one row rather than in a panel of their own: they are read
 * once on the way to the log, which is the thing anyone opened this tab for.
 */
function DeploymentHeader({
  deployment,
  onCancel,
  cancelling,
}: {
  deployment: Deployment;
  onCancel?: () => void;
  cancelling: boolean;
}) {
  const { t } = useTranslation("app");
  return (
    <div className="space-y-4 border border-line bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <DeploymentBadge status={deployment.status} />
            <h3 className="truncate text-sm text-fg">{deployment.title}</h3>
          </div>

          {deployment.commit && (
            <p className="mt-2 flex items-start gap-2 text-sm">
              <Icon name="branch" className="mt-1 size-3.5 shrink-0 text-fg-subtle" />
              <span className="min-w-0">
                <Mono className="text-fg">{deployment.commit.shortSha}</Mono>
                {deployment.commit.message && (
                  <span className="ml-2 text-fg-muted">{deployment.commit.message}</span>
                )}
              </span>
            </p>
          )}
        </div>

        {onCancel && (
          <Button variant="secondary" size="sm" loading={cancelling} onClick={onCancel}>
            {t("deployments.cancel")}
          </Button>
        )}
      </div>

      <dl className="flex flex-wrap gap-x-10 gap-y-3 border-t border-line pt-4">
        <Fact label={t("deployments.started")}>
          {absoluteTime(deployment.startedAt ?? deployment.createdAt)}
        </Fact>
        <Fact label={t("deployments.duration")}>
          {duration(deployment.startedAt, deployment.finishedAt)}
        </Fact>
        <Fact label={t("deployments.trigger")}>{deployment.trigger ?? "—"}</Fact>
        <Fact label={t("deployments.by")}>{deployment.triggeredBy ?? "—"}</Fact>
      </dl>

      {deployment.errorMessage && (
        <p className="border-l-2 border-l-danger pl-3 text-sm text-fg-muted">
          {deployment.errorMessage}
        </p>
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="micro">{label}</dt>
      <dd className="mt-1 text-sm text-fg tabular-nums">{children}</dd>
    </div>
  );
}

function DeploymentLog({
  orgId,
  appId,
  deploymentId,
}: {
  orgId: string | undefined;
  appId: string | undefined;
  deploymentId: string;
}) {
  const { t } = useTranslation("app");
  const { data, isPending, error } = useDeploymentLogs(orgId, appId, deploymentId);

  if (isPending) return <LoadingBlock label={t("deployments.loading")} />;
  if (error) return <Alert>{error.message}</Alert>;

  return (
    <LogViewer
      logs={data?.logs ?? ""}
      streaming={Boolean(data && !data.complete)}
      downloadName={`deployment-${deploymentId}`}
      emptyMessage={t("deployments.noLogs")}
    />
  );
}
